import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { Plus, Trash2, Save } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#0077B6]";
const ICON_OPTIONS = ["Sparkles", "Snowflake", "Truck", "ShieldCheck", "Heart"];

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { reload } = useSettings();

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) return <div>Yükleniyor...</div>;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setFeature = (i, k, v) => {
    const f = [...form.features]; f[i] = { ...f[i], [k]: v }; set("features", f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", { ...form, bus_fee: Number(form.bus_fee) });
      toast.success("Site ayarları kaydedildi");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const Section = ({ title, desc, children }) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-6">
      <h2 className="font-display text-lg font-extrabold">{title}</h2>
      {desc && <p className="text-xs text-slate-500 mb-4 mt-1">{desc}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
  const Field = ({ label, children }) => (
    <label className="block text-sm">
      <span className="text-xs font-semibold text-slate-600 mb-1 inline-block">{label}</span>
      {children}
    </label>
  );

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Site Ayarları</h1>
          <p className="text-sm text-slate-500 mt-1">Anasayfa, hakkımızda, iletişim ve genel sayfa içeriklerini buradan düzenleyin.</p>
        </div>
        <button onClick={submit} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="save-settings">
          <Save size={16}/> {saving ? "Kaydediliyor..." : "Tüm Değişiklikleri Kaydet"}
        </button>
      </div>

      <form onSubmit={submit} className="space-y-5 max-w-4xl">
        <Section title="Hero Bölümü" desc="Anasayfanın üstündeki büyük başlık alanı.">
          <Field label="Başlık"><input className={inputCls} value={form.hero_title} onChange={(e) => set("hero_title", e.target.value)} /></Field>
          <Field label="Alt başlık"><textarea rows={3} className={inputCls} value={form.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} /></Field>
          <Field label="Hero görsel URL"><input className={inputCls} value={form.hero_image_url} onChange={(e) => set("hero_image_url", e.target.value)} /></Field>
          {form.hero_image_url && <img src={form.hero_image_url} alt="" className="rounded-xl max-h-40 object-cover" />}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Birincil buton metni"><input className={inputCls} value={form.hero_cta_primary_label} onChange={(e) => set("hero_cta_primary_label", e.target.value)} /></Field>
            <Field label="İkincil buton metni"><input className={inputCls} value={form.hero_cta_secondary_label} onChange={(e) => set("hero_cta_secondary_label", e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Özellikler" desc="Anasayfadaki avantaj kartları (5 adet önerilir).">
          {(form.features || []).map((f, i) => (
            <div key={i} className="grid md:grid-cols-12 gap-2 p-3 border rounded-xl">
              <input className={inputCls + " md:col-span-3"} placeholder="Başlık" value={f.title} onChange={(e) => setFeature(i, "title", e.target.value)} />
              <input className={inputCls + " md:col-span-6"} placeholder="Açıklama" value={f.description} onChange={(e) => setFeature(i, "description", e.target.value)} />
              <select className={inputCls + " md:col-span-2"} value={f.icon} onChange={(e) => setFeature(i, "icon", e.target.value)}>
                {ICON_OPTIONS.map((opt) => <option key={opt}>{opt}</option>)}
              </select>
              <button type="button" onClick={() => set("features", form.features.filter((_, j) => j !== i))} className="text-rose-600 md:col-span-1"><Trash2 size={15}/></button>
            </div>
          ))}
          <button type="button" onClick={() => set("features", [...(form.features || []), { title: "", description: "", icon: "Sparkles" }])} className="text-sm text-[#0077B6] font-bold flex items-center gap-1"><Plus size={14}/> Özellik ekle</button>
        </Section>

        <Section title="İletişim Bilgileri" desc="Header, footer ve iletişim sayfasında görünür.">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Telefon"><input className={inputCls} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
            <Field label="WhatsApp (ülke kodlu, +/boşluk yok — örn: 905551234567)"><input className={inputCls} value={form.contact_whatsapp} onChange={(e) => set("contact_whatsapp", e.target.value)} /></Field>
            <Field label="E-posta"><input className={inputCls} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <Field label="Adres"><input className={inputCls} value={form.contact_address} onChange={(e) => set("contact_address", e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Hakkımızda" desc="Anasayfanın altında ve Hakkımızda sayfasında görünür.">
          <Field label="Başlık"><input className={inputCls} value={form.about_title} onChange={(e) => set("about_title", e.target.value)} /></Field>
          <Field label="İçerik"><textarea rows={5} className={inputCls} value={form.about_content} onChange={(e) => set("about_content", e.target.value)} /></Field>
          <Field label="Görsel URL"><input className={inputCls} value={form.about_image_url} onChange={(e) => set("about_image_url", e.target.value)} /></Field>
          {form.about_image_url && <img src={form.about_image_url} alt="" className="rounded-xl max-h-40 object-cover" />}
        </Section>

        <Section title="Footer" desc="Sayfanın altındaki açıklama metni.">
          <Field label="Footer açıklaması"><textarea rows={3} className={inputCls} value={form.footer_description} onChange={(e) => set("footer_description", e.target.value)} /></Field>
        </Section>

        <Section title="Teslimat & Ücret" desc="Otobüs ücreti her siparişe sabit olarak eklenir.">
          <Field label="Otobüs ücreti (TL)"><input type="number" step="1" className={inputCls} value={form.bus_fee} onChange={(e) => set("bus_fee", e.target.value)} /></Field>
        </Section>

        <button type="submit" disabled={saving} className="btn-primary w-full disabled:opacity-60"><Save size={16}/> Tüm Değişiklikleri Kaydet</button>
      </form>
    </div>
  );
}
